import React, { useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { uid, sceneKey } from "@/components/shownotes/meta";
import SceneListRow from "@/components/shownotes/SceneListRow";
import SceneReviewList from "@/components/shownotes/SceneReviewList";

const toItems = (scenes) => scenes.map((s) => ({ id: uid(), name: s.name, original: s.name, sources: [s.name], uses: s.uses }));

// Director edits a production's ordered scene list; renames/merges rewrite the production's past notes on save.
export default function SceneListDialog({ production, open, onOpenChange, onSaved }) {
  const [items, setItems] = useState(null);
  const [review, setReview] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [saved, setSaved] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    if (!open || !production) return;
    setItems(null); setDraft("");
    api.get("/show-notes/scene-list", { params: { production_id: production.id } })
      .then(({ data }) => { setItems(toItems(data.scenes)); setReview(data.review); setHidden(data.hidden); setSaved(data.saved); })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onOpenChange(false); });
  }, [open, production?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (id, p) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, ...p } : x)));
  const move = (from, to) => setItems((xs) => { const a = [...xs]; a.splice(to, 0, a.splice(from, 1)[0]); return a; });
  const remove = (it) => { setItems((xs) => xs.filter((x) => x.id !== it.id)); if (it.uses) setHidden((h) => [...h, ...it.sources]); };
  const drop = (r) => setReview((rs) => rs.filter((x) => x !== r));
  const addNew = () => {
    const name = draft.trim();
    if (!name) return;
    if (items.some((x) => sceneKey(x.name) === sceneKey(name))) { toast.error("That scene is already on the list"); return; }
    setItems((xs) => [...xs, { id: uid(), name, original: null, sources: [], uses: 0 }]); setDraft("");
  };
  const review_ = {
    onAdd: (r) => { setItems((xs) => [...xs, { id: uid(), name: r.name, original: r.name, sources: [r.name], uses: r.uses }]); drop(r); },
    onMerge: (r, id) => { setItems((xs) => xs.map((x) => (x.id === id ? { ...x, sources: [...x.sources, r.name], uses: x.uses + r.uses } : x))); drop(r); },
    onDismiss: (r) => { setHidden((h) => [...h, r.name]); drop(r); },
  };
  const drag = {
    start: setDragIdx, end: () => setDragIdx(null),
    over: (i) => { if (dragIdx == null || dragIdx === i) return; move(dragIdx, i); setDragIdx(i); },
  };
  const dupOf = (i) => { const k = sceneKey(items[i].name); const j = items.findIndex((x) => sceneKey(x.name) === k); return k && j < i ? j + 1 : null; };

  const save = async () => {
    if (items.some((x) => !x.name.trim())) { toast.error("Give every scene a name or remove it"); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/show-notes/scene-list", { production_id: production.id, scenes: items.map(({ name, sources }) => ({ name, sources })), hidden });
      toast.success(`Scene list saved${data.updated_notes ? ` · updated ${data.updated_notes} past note${data.updated_notes === 1 ? "" : "s"}` : ""}`);
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] max-w-2xl overflow-y-auto bg-slate-50" data-testid="scene-list-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Scene list · {production?.name}</DialogTitle>
          <DialogDescription>Stage managers pick scenes from this list, in this order. Renaming a scene updates it in every past note for this production.</DialogDescription>
        </DialogHeader>
        {!items ? <div className="flex justify-center py-10 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
          <div className="space-y-4">
            <p className={`w-fit rounded-full px-2.5 py-0.5 text-xs font-semibold ${saved ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`} data-testid="scene-list-status">
              {saved ? "Saved list" : "Not saved yet · pre-filled from earlier notes"}
            </p>
            <div className="space-y-2" data-testid="scene-list-rows">
              {items.map((it, i) => (
                <SceneListRow key={it.id} item={it} i={i} last={i === items.length - 1} dupOf={dupOf(i)} dragging={dragIdx === i} drag={drag}
                  onName={(name) => patch(it.id, { name })} onMove={(d) => move(i, i + d)} onRemove={() => remove(it)} />
              ))}
              {!items.length && <p className="py-4 text-center text-sm text-slate-400" data-testid="scene-list-empty">No scenes yet. Add your first scene below.</p>}
            </div>
            <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); addNew(); }}>
              <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Add a scene, e.g. Act 1 Scene 3" className="bg-white" data-testid="scene-list-new-input" />
              <Button type="submit" variant="outline" className="rounded-full bg-white" data-testid="scene-list-add-btn"><Plus className="mr-1 h-4 w-4" /> Add scene</Button>
            </form>
            <SceneReviewList review={review} items={items} {...review_} />
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="scene-list-cancel-btn">Cancel</Button>
          <Button className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={!items || busy} onClick={save} data-testid="scene-list-save-btn">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save list
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
